import { VdsButton, VdsIcon } from '@volt/volt-extended-components-react';
import { ChangeEvent, ReactElement, useCallback, useEffect, useRef, CSSProperties } from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import styles from './CasFormFilePicker.module.scss';
import { useTranslation } from 'react-i18next';
import CasFormFileInfo from '../cas-form-file-info/CasFormFileInfo';
import classNames from 'classnames';

export interface CasFormFilePickerProps<TFormValues extends FieldValues>
  extends UseControllerProps<TFormValues> {
  accept: string;
  minHeight?: string;
}

export const CasFormFilePicker = <TFormValues extends FieldValues>({
  disabled,
  accept,
  minHeight,
  ...sanitisedProps
}: CasFormFilePickerProps<TFormValues>): ReactElement => {
  const { t } = useTranslation();

  const { field, fieldState, formState } = useController(sanitisedProps);

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Catch the cancel button on the native File Open dialog.
    const callback = () => field.onBlur();
    const currentRef = fileRef.current;

    currentRef?.addEventListener('cancel', callback);

    return () => {
      currentRef?.removeEventListener('cancel', callback);
    };
  }, [field, fileRef]);

  const onClickSelectFile = useCallback(() => {
    if (fileRef.current) {
      fileRef.current.value = '';
      fileRef.current.click();
    }
  }, []);

  // Fixme: Use RHF types to avoid type casting if possible.
  const fieldValue = field.value as File | undefined;
  const fileSet = fieldState.isTouched && !fieldState.invalid && fieldValue !== undefined;

  const onSetFile = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      if (fileRef.current && evt.currentTarget.files) {
        field.onChange(evt.currentTarget.files[0]);
      }
    },
    [field],
  );

  const onRemoveFile = useCallback(() => {
    field.onChange(undefined);
  }, [field]);

  return (
    <div className={styles.file}>
      <div
        className={classNames(styles.file__field, {
          [styles['file__field--error']]: fieldState.error !== undefined,
        })}
        style={{ '--cas-file-picker-min-height': minHeight } as CSSProperties}
      >
        {/* Visually hidden file input. */}
        <input
          ref={fileRef}
          style={{ display: 'none' }}
          type="file"
          multiple={false}
          accept={accept}
          onChange={onSetFile}
        />
        {fieldValue ? (
          <CasFormFileInfo
            name={fieldValue.name}
            size={fieldValue.size}
            onRemove={onRemoveFile}
            disabled={disabled || field.disabled || formState.isSubmitting}
          />
        ) : (
          <VdsButton
            variant="primary"
            size="medium"
            onClick={onClickSelectFile}
            disabled={disabled || field.disabled || fileSet || formState.isSubmitting}
            onBlur={field.onBlur}
          >
            {t('SelectFile')}
            <VdsIcon slot="icon-left" svg-icon="upload" />
          </VdsButton>
        )}
      </div>
      {!(disabled || field.disabled) && fieldState.error?.message && (
        <span className={styles.file__errorMessage}>{fieldState.error.message}</span>
      )}
    </div>
  );
};

export default CasFormFilePicker;
